import { useState } from 'react'
import type { ModuleProposal } from './knowledgeTypes'

type Row = {
  tempId: string
  name: string
  aliases: string
  action: 'keep' | 'add' | 'rename'
  matchedAreaId?: number
  removed: boolean
}

function splitAliases(value: string): string[] {
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

export function ModuleReviewPanel({
  projectId,
  runId,
  proposal,
  onSubmit,
  onDone,
}: {
  projectId: number
  runId: number
  proposal: ModuleProposal
  onSubmit: (
    projectId: number,
    runId: number,
    body: {
      decisions: Array<{
        tempId: string
        decision: 'accept' | 'reject'
        edited?: { name?: string; aliases?: string[] }
      }>
      added?: Array<{ name: string; aliases?: string[] }>
      deleted?: number[]
    }
  ) => Promise<unknown>
  onDone: () => void
}) {
  const [rows, setRows] = useState<Row[]>(() =>
    proposal.modules.map((m) => ({
      tempId: m.tempId,
      name: m.name,
      aliases: m.aliases.join(', '),
      action: m.action,
      matchedAreaId: m.matchedAreaId,
      removed: false,
    }))
  )
  const [newRows, setNewRows] = useState<Array<{ name: string; aliases: string }>>([])
  const [busy, setBusy] = useState(false)

  const originalMap = new Map(proposal.modules.map((m) => [m.tempId, m]))

  const updateRow = (tempId: string, patch: Partial<Row>) => {
    setRows((prev) => prev.map((r) => (r.tempId === tempId ? { ...r, ...patch } : r)))
  }

  const toggleRemoved = (tempId: string) => {
    setRows((prev) => prev.map((r) => (r.tempId === tempId ? { ...r, removed: !r.removed } : r)))
  }

  const handleConfirm = async () => {
    setBusy(true)
    try {
      const decisions = rows.map((row) => {
        if (row.removed) return { tempId: row.tempId, decision: 'reject' as const }
        const original = originalMap.get(row.tempId)
        const editedName = row.name.trim() !== original?.name
        const editedAliases =
          splitAliases(row.aliases).join(',') !== (original?.aliases ?? []).join(',')
        const edited =
          editedName || editedAliases
            ? {
                ...(editedName ? { name: row.name.trim() } : {}),
                ...(editedAliases ? { aliases: splitAliases(row.aliases) } : {}),
              }
            : undefined
        return { tempId: row.tempId, decision: 'accept' as const, ...(edited ? { edited } : {}) }
      })
      const added = newRows
        .filter((r) => r.name.trim())
        .map((r) => ({ name: r.name.trim(), aliases: splitAliases(r.aliases) }))
      await onSubmit(projectId, runId, { decisions, added })
      onDone()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-3 p-4">
      <h3 className="text-sm font-semibold text-harbor-text">Proposed modules</h3>
      <p className="text-xs text-harbor-text-muted">
        Review the discovered modules. Remove any you don’t want, edit names or aliases, and add new
        ones.
      </p>
      <div className="space-y-2">
        {rows.map((row) => (
          <div
            key={row.tempId}
            className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${row.removed ? 'border-red-500/30 bg-red-500/5 opacity-60' : 'border-harbor-border bg-harbor-surface-raised'}`}
          >
            <div className="flex-1 space-y-1">
              <input
                value={row.name}
                onChange={(e) => updateRow(row.tempId, { name: e.target.value })}
                disabled={row.removed}
                className="w-full rounded border border-harbor-border bg-harbor-surface px-2 py-1 text-sm text-harbor-text focus:border-emerald-500 focus:outline-none"
              />
              <input
                value={row.aliases}
                onChange={(e) => updateRow(row.tempId, { aliases: e.target.value })}
                placeholder="aliases, comma separated"
                disabled={row.removed}
                className="w-full rounded border border-harbor-border bg-harbor-surface px-2 py-1 text-xs text-harbor-text-secondary focus:border-emerald-500 focus:outline-none"
              />
              <div className="flex items-center gap-1">
                <span className="rounded bg-harbor-border px-1.5 py-0.5 text-[10px] uppercase text-harbor-text-secondary">
                  {row.action}
                </span>
                <span className="text-[10px] text-harbor-text-muted">{row.tempId}</span>
              </div>
            </div>
            <button
              type="button"
              aria-label={`remove ${row.name}`}
              onClick={() => toggleRemoved(row.tempId)}
              className={`rounded px-2 py-1 text-xs ${row.removed ? 'bg-harbor-border text-harbor-text' : 'text-red-400 hover:text-red-300'}`}
            >
              {row.removed ? 'Undo' : 'Remove'}
            </button>
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <h4 className="text-xs font-semibold text-harbor-text-muted">Add module</h4>
        {newRows.map((r, idx) => (
          <div key={idx} className="flex items-center gap-2">
            <input
              value={r.name}
              onChange={(e) =>
                setNewRows((prev) =>
                  prev.map((v, i) => (i === idx ? { ...v, name: e.target.value } : v))
                )
              }
              placeholder="New module name"
              className="flex-1 rounded border border-harbor-border bg-harbor-surface px-2 py-1 text-sm text-harbor-text"
            />
            <input
              value={r.aliases}
              onChange={(e) =>
                setNewRows((prev) =>
                  prev.map((v, i) => (i === idx ? { ...v, aliases: e.target.value } : v))
                )
              }
              placeholder="aliases"
              className="flex-1 rounded border border-harbor-border bg-harbor-surface px-2 py-1 text-sm text-harbor-text"
            />
            <button
              type="button"
              onClick={() => setNewRows((prev) => prev.filter((_, i) => i !== idx))}
              className="text-xs text-harbor-text-muted"
            >
              Remove
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => setNewRows((prev) => [...prev, { name: '', aliases: '' }])}
          className="text-xs font-medium text-emerald-400 hover:text-emerald-300"
        >
          + Add module
        </button>
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => void handleConfirm()}
          disabled={busy}
          className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
        >
          Confirm modules
        </button>
      </div>
    </div>
  )
}
